/**
 * Love Loyalty Program Integration
 * @description Handles loyalty program UI initialization and customer state detection
 * @version 2.1.0
 */
(function () {
  "use strict";

  /**
   * Check if customer is logged in
   * @returns {boolean}
   */
  function isCustomerLoggedIn() {
    // Most reliable: Check Liquid-set flag
    if (window.customerLoggedIn === true) {
      return true;
    }

    // Check if on account page (not login/register)
    const path = window.location.pathname;
    if (
      path.startsWith("/account") &&
      !path.includes("/login") &&
      !path.includes("/register") &&
      !path.includes("/activate")
    ) {
      return true;
    }

    // Check Shopify global
    if (window.Shopify?.customerId) {
      return true;
    }

    // Check account link text - if it says "Account" user is logged in
    const accountLink = document.querySelector('a[href="/account"]');
    if (accountLink) {
      const linkText = accountLink.textContent.trim().toLowerCase();
      if (
        linkText === "account" ||
        linkText === "my account" ||
        linkText === "konto" ||
        linkText === "mein konto"
      ) {
        return true;
      }
    }

    return false;
  }

  /**
   * Create Sign Up / Login action buttons
   * @returns {HTMLElement}
   */
  function createActionButtons() {
    const actionsContainer = document.createElement("div");
    actionsContainer.className = "ll__product-actions";

    const signUpLink = document.createElement("a");
    signUpLink.href = "/account/register";
    signUpLink.textContent = "SIGN UP";

    const loginLink = document.createElement("a");
    loginLink.href = "/account/login";
    loginLink.textContent = "LOGIN";

    actionsContainer.appendChild(signUpLink);
    actionsContainer.appendChild(loginLink);

    return actionsContainer;
  }

  /**
   * Create the loyalty program header
   * @returns {HTMLElement}
   */
  function createHeader() {
    const header = document.createElement("div");
    header.className = "loyalty-program-header";

    const icon = document.createElement("div");
    icon.className = "loyalty-program-icon";

    const title = document.createElement("h3");
    title.className = "loyalty-program-title";
    title.textContent = "Spend & Earn Points";

    header.appendChild(icon);
    header.appendChild(title);

    return header;
  }

  /**
   * Handle action buttons visibility based on login state
   * @param {HTMLElement} block - The loyalty program block
   * @param {boolean} customerLoggedIn - Whether customer is logged in
   */
  function handleActionButtons(block, customerLoggedIn) {
    const existingActions = block.querySelector(".ll__product-actions");

    if (customerLoggedIn) {
      // Hide buttons if customer is logged in
      if (existingActions) {
        existingActions.style.display = "none";
      }
    } else {
      // Customer is logged out
      if (existingActions) {
        // Show existing buttons
        existingActions.style.display = "flex";
      } else {
        // Create new buttons
        const productContainer = block.querySelector(
          '[class*="ll__product"], .ll__container'
        );

        if (productContainer) {
          const actionsContainer = createActionButtons();
          const topRow = block.querySelector(".loyalty-program-top-row");

          if (topRow) {
            topRow.parentNode.insertBefore(actionsContainer, topRow.nextSibling);
          } else {
            productContainer.parentNode.insertBefore(
              actionsContainer,
              productContainer.nextSibling
            );
          }
        }
      }
    }
  }

  /**
   * Initialize a single loyalty program block
   * @param {HTMLElement} block - The loyalty program block element
   */
  function initBlock(block) {
    const customerLoggedIn = isCustomerLoggedIn();

    // If already processed, just handle button visibility
    if (block.dataset.loyaltyProcessed === "true") {
      handleActionButtons(block, customerLoggedIn);
      return;
    }

    // Find the product container
    const productContainer = block.querySelector(
      '[class*="ll__product"], .ll__container'
    );

    if (!productContainer) {
      return;
    }

    // Hide the SVG icon
    const svg = productContainer.querySelector("svg");
    if (svg) {
      svg.style.display = "none";
    }

    // Add header with icon and title (only once)
    if (!block.querySelector(".loyalty-program-top-row")) {
      const topRow = document.createElement("div");
      topRow.className = "loyalty-program-top-row";

      const header = createHeader();
      topRow.appendChild(header);

      // Move product container into the top row (right column)
      topRow.appendChild(productContainer);

      // Insert the top row at the beginning
      const firstChild = block.querySelector(
        'script, [class*="ll__product"], .ll__container'
      );

      if (firstChild && firstChild.parentNode === block) {
        block.insertBefore(topRow, firstChild);
      } else {
        block.insertBefore(topRow, block.firstChild);
      }
    }

    // Handle action buttons
    handleActionButtons(block, customerLoggedIn);

    // Mark as processed
    block.dataset.loyaltyProcessed = "true";
  }

  /**
   * Initialize all loyalty program blocks
   */
  function initLoyaltyProgram() {
    const loyaltyBlocks = document.querySelectorAll(
      '[id*="love_loyalty_program"], [class*="love_loyalty_program"], .shopify-app-block[id*="love_loyalty_program"]'
    );

    loyaltyBlocks.forEach(initBlock);
  }

  /**
   * Set up mutation observer for dynamic content
   */
  function setupObserver() {
    const observer = new MutationObserver(function (mutations) {
      let shouldRun = false;

      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;

          if (
            (node.id && node.id.includes("love_loyalty_program")) ||
            (node.querySelector &&
              node.querySelector('[id*="love_loyalty_program"]')) ||
            (node.classList && node.classList.contains("ll__product")) ||
            (node.classList && node.classList.contains("ll__product-actions"))
          ) {
            shouldRun = true;
          }
        });
      });

      if (shouldRun) {
        setTimeout(initLoyaltyProgram, 50);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  // Initialize immediately
  initLoyaltyProgram();

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      setTimeout(initLoyaltyProgram, 100);
    });
  } else {
    setTimeout(initLoyaltyProgram, 100);
  }

  // Set up observer for async content
  setupObserver();

  // Final check after page load
  window.addEventListener("load", function () {
    setTimeout(initLoyaltyProgram, 200);
  });
})();